import { useRef, useState } from 'react';
import { Stack, Typography, Button as MuiButton, IconButton, Box } from '@mui/material';
import { NotesPopover } from '@/components/tour/notes/components/NotesPopover';
import { Button } from '@/components/ui/button';
import { useBoardContext } from '../../../../hooks/useBoardContext';
import { GroupSelect } from '@/components/common/GroupSelect';
import { CountBadge } from '@/components/tour/notes/components/ui/CountBadge';
import { filterGroupHeight } from '@/components/tour/notes/data';
import { Switch } from '@/components/ui/switch';

export function Filters() {
  const anchorElRef = useRef<HTMLButtonElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const {
    boardFilters: {
      filters,
      setFilters,
      clearFilter,
      filterGroups,
      getFiltersCount,
      selectedFilterGroup,
      setSelectedFilterGroup,
    },
    setApplyFiltersToBacklog,
    applyFiltersToBacklog,
  } = useBoardContext();

  const groupFiltersCount = getFiltersCount();

  return (
    <>
      <Button
        ref={anchorElRef}
        onClick={() => {
          setIsOpen(!isOpen);
        }}
        sx={{
          gap: '0px',
          paddingX: '12px',
          color: '#101828',
          minWidth: groupFiltersCount > 0 ? '120px' : '80px',
          transition: 'min-width 0.3s ease-in-out',
          position: 'relative',
          '@container board-content (max-width: 1280px)': {
            minWidth: '44px',
            px: '0',
            width: '44px',
            height: '44px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            '& .button-text': {
              display: 'none',
            },
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            mr: '4px',
            '@container board-content (max-width: 1280px)': {
              mr: '0',
            },
          }}
        >
          <i className="fa-light fa-filter" style={{ fontSize: '18px', color: '#667085' }} />
        </Box>
        <span className="button-text">Filter</span>

        {groupFiltersCount > 0 && (
          <>
            <Typography
              sx={{
                color: '#2055FF',
                textAlign: 'center',
                fontFamily: 'Inter',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 500,
                lineHeight: '20px',
                ml: '12px',
                opacity: groupFiltersCount > 0 ? 1 : 0,
                transition: 'opacity 0.3s ease-in-out',
                '@container board-content (max-width: 1280px)': {
                  display: 'none',
                },
              }}
            >
              {groupFiltersCount}
            </Typography>

            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                clearFilter();
              }}
              sx={{
                width: '20px',
                height: '20px',
                ml: '10px',
                opacity: groupFiltersCount > 0 ? 1 : 0,
                transition: 'opacity 0.3s ease-in-out',
                '@container board-content (max-width: 1280px)': {
                  display: 'none',
                },
              }}
            >
              <i className="fa-regular fa-xmark" style={{ color: '#2055FF66', fontSize: '20px' }} />
            </IconButton>
          </>
        )}

        {groupFiltersCount > 0 && (
          <CountBadge
            count={groupFiltersCount}
            sx={{
              position: 'absolute',
              top: '-2px',
              right: '-2px',
              width: '13px',
              height: '13px',
              color: '#fff',
              backgroundColor: '#2055FF',
              fontSize: '10px',
              display: 'none',
              '@container board-content (max-width: 1280px)': {
                display: 'flex',
              },
            }}
          />
        )}
      </Button>

      <NotesPopover
        title="Filter"
        icon="fa-light fa-filter"
        anchorEl={anchorElRef.current}
        open={isOpen}
        onOpenChange={setIsOpen}
        anchorOrigin={{
          horizontal: 'right',
          vertical: 'bottom',
        }}
        transformOrigin={{
          horizontal: 'right',
          vertical: 'top',
        }}
        PaperProps={{
          sx: {
            marginTop: '6px',
          },
        }}
        headerStartContent={groupFiltersCount > 0 ? <CountBadge count={groupFiltersCount} /> : null}
        headerEndContent={
          Object.values(filters).some((filter) => !!filter.length) ? (
            <MuiButton
              onClick={() => {
                clearFilter();
              }}
              disableElevation
              variant="text"
              disableRipple
              sx={{
                all: 'unset',
                color: 'var(--Gray-500, #667085)',
                fontFamily: 'Inter',
                fontSize: '16px',
                fontStyle: 'normal',
                fontWeight: 500,
                lineHeight: '24px',
                paddding: '0px !important',
                '&:hover': {
                  backgroundColor: 'transparent',
                  color: 'var(--Gray-500, #667085)',
                },
              }}
            >
              Clear all
            </MuiButton>
          ) : null
        }
      >
        <GroupSelect
          mui={{
            sx: { height: filterGroupHeight[selectedFilterGroup] ?? '180px', width: '413px' },
          }}
          onGroupChange={(v) => setSelectedFilterGroup(v)}
          value={filters}
          groups={filterGroups}
          isMulti
          onChange={(v) => {
            setFilters(v);
          }}
        />

        <Stack
          direction="row"
          sx={{
            width: '100%',
            p: '10px 14px',
            height: '59px',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderTop: '1px solid var(--Gray-200, #EAECF0)',
          }}
        >
          <Typography
            sx={{
              color: 'var(--Gray-500, #667085)',
              fontFamily: 'Inter',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 500,
              lineHeight: '32px',
              letterSpacing: '0.1px',
            }}
          >
            Apply to Backlog
          </Typography>

          <Switch
            checked={applyFiltersToBacklog}
            onChange={(e, checked) => setApplyFiltersToBacklog(checked)}
          />
        </Stack>
      </NotesPopover>
    </>
  );
}
